import { UserPreference } from '@core/models/preference/user-preference';
import { IPreferenceState, initialPreferenceState } from './preference-state';
import { UserCompanyPreference } from '@core/models/preference/user-company-preference';
import { LocationPreference } from '@core/models/preference/location-preference';
import {
    PREFERENCE_COMPANY_SET,
    PREFERENCE_CONNECTIONS_SET,
    PREFERENCE_EXPORT_REPORT_SET,
    PREFERENCE_LOCATION_SET,
    PREFERENCE_MERGE,
    PREFERENCE_MULTI_LOCATION_SET,
    PREFERENCE_SET,
    PREFERENCE_SORT_GROUP_SET,
    PREFERENCE_UNIT_MEASUREMENT_SET
} from '../action-types';
import { SetPreferenceAction, SetSortGroupPreferenceAction } from './preference-actions';
import { deepmerge, overwriteMerge } from '@core/utils/merge';
import { Location } from '@core/models/location/location';
import { ConnectionListPreference } from '@core/models/preference/connection-list-preference';

export function reducer(
    state = initialPreferenceState(),
    action: any
): IPreferenceState {
    let prevPreference: UserPreference;
    let prevCompanyPreference: UserCompanyPreference;
    let prevCompanyPreferenceCollection: {
        [id: string]: UserCompanyPreference;
    };
    let prevLocationPreferenceCollection: {
        [id: string]: LocationPreference;
    };

    switch (action.type) {
        case PREFERENCE_SET: {
            const { user, company } = (action as SetPreferenceAction).payload;

            let nextState: IPreferenceState = {
                ...state,
            };

            if (user) {
                nextState['user'] = {
                    ...user,
                };
            }

            if (company) {
                nextState['company'] = {
                    ...company,
                };
            }

            return nextState;
        }

        case PREFERENCE_MERGE: {

            const { user, company } = (action as SetPreferenceAction).payload;

            let nextState: IPreferenceState = {
                ...state,
            };

            if (user) {
                nextState['user'] = deepmerge(state.user, user, {
                    arrayMerge: overwriteMerge,
                });
            }

            if (company) {
                nextState['company'] = deepmerge(state.company, company, {
                    arrayMerge: overwriteMerge,
                });
            }

            return nextState;
        }

        case PREFERENCE_LOCATION_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            const prevLocationPreferences: any = prevPreference.location || {};
            const locationPreference: Location = action.payload.location;
            prevCompanyPreference =
                prevCompanyPreferenceCollection[locationPreference.company_id];
            const prevLocationPreference =
                prevLocationPreferences[locationPreference.id];

            // Location preference
            const preferenceUpdate: any = {
                id: locationPreference.id,
                name: locationPreference.name,
            };
            if (action.payload.scheduleId) {
                preferenceUpdate.scheduleId = action.payload.scheduleId;
            }

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [locationPreference.company_id]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                {
                                    id: locationPreference.company_id,
                                    locationId: locationPreference.id,
                                    previousLocationId:
                                        prevCompanyPreference &&
                                        prevCompanyPreference.locationId,
                                    // TODO: Update isMultiLocation here from entity changes in company
                                }
                            ),
                        },
                        location: {
                            ...prevLocationPreferences,
                            [locationPreference.id]: Object.assign(
                                new LocationPreference(),
                                prevLocationPreference,
                                preferenceUpdate
                            ),
                        },
                    },
                },
            };

        case PREFERENCE_COMPANY_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            prevCompanyPreference =
                prevCompanyPreferenceCollection[action.payload.companyId];

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [action.payload.companyId]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                action.payload.preference
                            ),
                        },
                    },
                },
            };

        case PREFERENCE_SORT_GROUP_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            const {
                sort,
                group,
                userId,
            } = (action as SetSortGroupPreferenceAction).payload;

            return {
                ...state,
                user: {
                    ...state.user,
                    [userId]: {
                        ...prevPreference,
                        sort: sort || prevPreference.sort,
                        group: group || prevPreference.group,
                    },
                },
            };

        case PREFERENCE_MULTI_LOCATION_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            prevCompanyPreference =
                prevCompanyPreferenceCollection[action.payload.companyId];

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [action.payload.companyId]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                {
                                    isMultiLocation:
                                        action.payload.isMultiLocation,
                                }
                            ),
                        },
                    },
                },
            };

        case PREFERENCE_UNIT_MEASUREMENT_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            prevCompanyPreference =
                prevCompanyPreferenceCollection[action.payload.companyId];

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [action.payload.companyId]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                {
                                    unitMeasurement:
                                        action.payload.unitMeasurement,
                                }
                            ),
                        },
                    },
                },
            };

        case PREFERENCE_EXPORT_REPORT_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            prevCompanyPreference =
                prevCompanyPreferenceCollection[action.payload.companyId] ||
                new UserCompanyPreference();
            const prevExport: any = prevCompanyPreference.export || {};
            const prevReports: any = prevExport.reports || {};
            const prevReport: any = prevReports[action.payload.report] || {};

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [action.payload.companyId]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                {
                                    export: {
                                        ...prevExport,
                                        report: action.payload.report,
                                        reports: {
                                            ...prevReports,
                                            [action.payload.report]: {
                                                ...prevReport,
                                                ...action.payload.settings,
                                            },
                                        },
                                    },
                                }
                            ),
                        },
                    },
                },
            };

        case PREFERENCE_CONNECTIONS_SET:
            prevPreference =
                state.user[action.payload.userId] || new UserPreference();
            prevCompanyPreferenceCollection = prevPreference.company || {};
            prevCompanyPreference =
                prevCompanyPreferenceCollection[action.payload.companyId];

            let connectionList: ConnectionListPreference = {};

            if (prevCompanyPreference && prevCompanyPreference.connectionList) {
                connectionList = {
                    ...prevCompanyPreference.connectionList,
                    ...action.payload.connectionList,
                };
            } else {
                connectionList = {
                    ...action.payload.connectionList,
                };
            }

            return {
                ...state,
                user: {
                    ...state.user,
                    [action.payload.userId]: {
                        ...prevPreference,
                        company: {
                            ...prevCompanyPreferenceCollection,
                            [action.payload.companyId]: Object.assign(
                                new UserCompanyPreference(),
                                prevCompanyPreference,
                                {
                                    connectionList,
                                }
                            ),
                        },
                    },
                },
            };

        default:
            return state;
    }
}
